import {User} from "./user.model";
import {Pet} from "./pet.model";
import {VetDoctorSubscription} from "./vet-doctor-subscription-model";
import {VetReservation} from "./vet-reservation.model";
import {VetineoVisitTypeBundle} from "./vetineo-visit-type-bundle.model";

/**
 * Model agregujący (DTO) dostarczający kompletny pakiet danych startowych
 * niezbędnych do wyrenderowania interfejsu rezerwacji wizyt dla pacjenta.
 * Łączy słowniki personelu, usług, cenników oraz ramy czasowe w jeden obiekt.
 */
export class VetClinicData {


  /** Początek zakresu dat, dla którego pobrano dane dostępności kalendarza. */
  public fromDate: string;

  /** Koniec zakresu dat dla kalendarza. */
  public toDate: string;

  /** Tablica najbliższych dostępnych godzin pracy (wolnych terminów).
   */
  public closestWorkHours: ClosestWorkHour[];

  /** Pełna lista lekarzy weterynarii dostępnych do rezerwacji w zadanym okresie. */
  public doctors: Doctor[];

  /** Zoptymalizowana lista samych identyfikatorów lekarzy.
   */
  public doctorsUUIDS: DoctorUUID[];

  /** Tablica dostępnych typów wizyt (usług). */
  public visitTypes: VisitType[];

  /** Słownik usług pogrupowany według kategorii (Map).
   */
  public groupedVisitTypes: Map<string, VisitType[]>;

  /** Tablica pakietów usług (Bundles). */
  public vetineoVisitTypeBundles: VetineoVisitTypeBundle[];

  /** Flaga określająca, czy włączone są
   * nowe typy wizyt Vetineo
   */
  public useVetineoVisitTypes: boolean;

  /** Tablica dziennych grafików (Work Hour List Items).
   * Reprezentuje wygenerowaną siatkę kalendarza dla lekarzy. Każdy element
   * tej tablicy to "dniówka" / blok roboczy (np. dyżur od 08:00 do 16:00),
   * który wewnątrz zawiera już pocięte na mniejsze okienka (WorkHour) sloty,
   * gotowe do kliknięcia przez pacjenta w interfejsie rezerwacji.
   */
  public whListItems: WhListItem[];

  /** Czas w minutach, na jaki system "zamraża" (blokuje) termin w kalendarzu
   * po przejściu pacjenta do bramki płatności.
   */
  public paidBookingLockMinutes: number;

  constructor() {
  }
}

/**
 * Model typu wizyty z Vetineo.
 */
export class VetVisitType {
  public uuid: string;
  /** Identyfikator wizyty w KliniceXP */
  public klinikaXpVisitId: number;
  public vetcodeId: number;

  /** Czy wymagana jest przedpłata? */
  public prepaidRequired: boolean;
  /** Kwota przedpłaty. */
  public prepaidPrice: number;
  public online: boolean;
  public info: string;
  public multimediaEnabled: boolean;
  public communicationToolsEnabled: boolean;
}

/**
 * Model typu wizyty z systemu KlinikiXP.
 */
export class VisitType {
  public doctorId: number;
  public valid: boolean;
  public visitTypeId: number;
  public vetcodeId: number;
  /** Identyfikator typu wizyty w KliniceXP. */
  public xid: number;
  /** Nazwa typu wizyty */
  public name: string;
  /** Standardowy czas typu wizyty (w minutach) */
  public countTime: number;
  /** Gatunki dedykowane tej usłudze */
  public petSpecies: string[];
  /** Typ wizyty przeznaczony dla wszystkich gatunków */
  public allPetSpecies: boolean;
  /** Czy wymagana jest przedpłata? */
  public prepaidRequired: boolean;
  /** Kwota przedpłaty. */
  public prepaidPrice: number;
  public online: boolean;
  /** Identyfikator powiązanego obiektu `VetDoctorVetVisitType` */
  public vetDoctorVetVisitTypeUUID: string;
  public bundleUUID;

  public info: string;

  /** * WZORZEC ADAPTER: Metoda transformująca format typu wizyty KlinikiXP na format Vetineo.
   */
  public asVetVisitType(): VetVisitType {
    let vetVisitType: VetVisitType = new VetVisitType();
    vetVisitType.online = this.online;
    vetVisitType.prepaidPrice = this.prepaidPrice;
    vetVisitType.prepaidRequired = this.prepaidRequired;
    vetVisitType.vetcodeId = this.vetcodeId;
    vetVisitType.klinikaXpVisitId = this.xid;
    vetVisitType.info = this.info;

    return vetVisitType;
  }

  constructor() {
  }
}

/** Model lekarza do kreatora rezerwacji. */
export class Doctor {
  public captionHint: string;
  public doctorId: number;
  public doctorName: string;
  public shortName: string;
  public stdVisitMinutes: number;
  public valid: boolean;
  public vetcodeId: number;

  public uuid: string;
  public activeSubscription: VetDoctorSubscription;
  public closestWorkHour: WorkHour;

  public closestTerm: string;
  public phoneClosestWorkHour: string;

  public prepaidRequired: boolean;
  public prepaidVisitPrice: number;

  constructor() {
  }
}

/** Proste DTO relacyjne: łączy identyfikatory lekarza z Vetineo i KlinikiXP. */
export class DoctorUUID {
  public doctorId: number;
  public uuid: string;
}

/** Wrapper łączący lekarza z jego pierwszym wolnym terminem. */
export class ClosestWorkHour {
  public doctorUUID: string;
  public workHour: WorkHour;
}

/**
 * Reprezentuje ciągły blok pracy (np. zmianę/dyżur) danego lekarza w danym dniu.
 * Agreguje mniejsze "sloty" w jedną listę.
 */
export class WhListItem {
  public doctorId: number;
  public hoursForDay: string;
  public startTime: string;
  public endTime: string;

  /** Limity długości wizyt (Visit Time). */
  public minVT: number;
  public maxVT: number;

  /** Czy blok pracy jest zablokowany? */
  public isLocked = false;
  /** Czy to dzień "Tylko Telefon"? */
  public phoneOnly = false;
  /** Czy to dzień "Tylko Wizyty Stacjonarne"? */
  public stationaryOnly = false;

  /** Tablica fizycznych, klikalnych okienek w kalendarzu. */
  public workHours: WorkHour[];
}

/**
 * Reprezentuje jeden, najmniejszy fizyczny slot w kalendarzu (np. konkretne 15 minut o 14:00).
 */
export class WorkHour {

  public idx: number;
  public isDefined = false;
  /** Flaga blokady progresywnej (Progressive Reservation).
   * Jeśli true, to slot jest wolny, ale pacjent nie może go wybrać,
   * bo stworzyłoby to okienko w grafiku lekarza.
   */
  public isLockedPR = false;
  public isBooked = false;
  public canBeBooked = true;
  public hoursForDay: string;
  public startDateTime: string;
  public endDateTime: string;
  public firstAvailableTerm: string;
  public whObjectId: number;
  public whObjectName: string;
  public tMinutes: number;
  public timeThickInMinutes: number;
  public delayFromNowMinutes: number;
  public maxVT: number;

  constructor() {
  }
}

/**
 * Obiekt koszyka / zlecenia rezerwacji.
 * Zbiera wszystkie dokonane przez pacjenta wybory na interfejsie
 * i przesyła je z powrotem na backend w celu sfinalizowania akcji.
 */
export class BookingData {
  public date: string;
  public doctor: Doctor;
  public workHour: WorkHour;
  public visitType: VisitType;
  public user: User;
  public pet: Pet;
  public message: string;

  /** * Metoda fabrykująca (Factory Method) - przekształca "koszyk"
   * w docelowy, twardy rekord w bazie danych (VetReservation).
   */
  public asVetReservation(comment: string) {
    return VetReservation.construct(this, comment);
  }
}
